import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';
import { parseCommandResult } from './command-result.ts';
import type { PhoneVerificationState } from '../data/resources/phone-number.type.ts';

const urlOptions: QueryUrlOptions = {
  identifier: { type: 'commands' },
  op: 'query',
  resourcePath: 'api/v2/commands/phone-verification',
};

export type PhoneVerificationErrorCode =
  | 'forbidden_principal'
  | 'invalid_phone_number'
  | 'invalid_verification_code'
  | 'missing_verification_code'
  | 'rate_limit_exceeded'
  | 'unsupported_region'
  | 'verification_locked_out'
  | 'verification_request_rejected'
  | 'verification_service_unavailable';

const phoneVerificationErrorCodes: ReadonlySet<string> = new Set([
  'forbidden_principal',
  'invalid_phone_number',
  'invalid_verification_code',
  'missing_verification_code',
  'rate_limit_exceeded',
  'unsupported_region',
  'verification_locked_out',
  'verification_request_rejected',
  'verification_service_unavailable',
] satisfies PhoneVerificationErrorCode[]);

export interface PhoneVerificationCommandError {
  type: PhoneVerificationErrorCode;
  detail: string;
}

/** The five surfaces a capture may originate from; sent so the funnel is attributed. */
export type PhoneVerificationSurface =
  'enrollment' | 'ssoModal' | 'postLoginOverlay' | 'profile' | 'supportPage';

/**
 * Viewport class, not client technology. The funnel reports the intent gradient per
 * platform and the post-login overlay rolls out mobile-first, so `desktop` / `mobile`
 * is what the dashboards group by.
 */
export type PhoneVerificationPlatform = 'desktop' | 'mobile';

/**
 * Attribution sent with every command. Both are optional on the wire: the server
 * records `unknown` rather than rejecting a request that omits them.
 */
export interface PhoneVerificationOrigin {
  surface?: PhoneVerificationSurface;
  platform?: PhoneVerificationPlatform;
}

/**
 * State of the *challenge* — whether a code is outstanding. Distinct from a number's
 * possession state (`PhoneVerificationState`), which is what the profile badge reads.
 */
export type PhoneChallengeState = 'awaiting_code' | 'verified' | 'not_verified';

/** The number as the server now holds it, so a caller can reconcile without refetching. */
export interface PhoneVerificationNumberPayload {
  id: string;
  number: string;
  /** Formatted for display; `number` stays E.164. */
  display: string;
  verificationState: PhoneVerificationState;
  /** ISO-8601, null until the number reaches `verified`. */
  verifiedAt: string | null;
  smsConsent: boolean;
}

/**
 * The retry/cooldown/lockout facts the server owns. Never hardcode these: a client
 * constant would drift from Twilio Verify's hosted TTL the moment either changed.
 * Timestamps are ISO-8601.
 */
export interface PhoneVerificationAttemptPayload {
  attemptsRemaining?: number;
  lockedUntil?: string;
  codeExpiresAt?: string;
  resendAvailableAt?: string;
}

export interface PhoneVerificationResult {
  /**
   * @deprecated The challenge state under its legacy name. Bind new code to
   * `challengeState`; both carry the same values.
   */
  verificationState: PhoneChallengeState;
  challengeState: PhoneChallengeState;
  phoneNumber?: PhoneVerificationNumberPayload;
  verification?: PhoneVerificationAttemptPayload;
}

export interface PhoneVerificationStartResult extends PhoneVerificationResult {
  destination: { kind: 'phone'; lastFour: string };
}

export interface PhoneVerificationCheckResult extends PhoneVerificationResult {
  phoneNumberId: string;
  phoneNumberChanged: boolean;
}

export type PhoneVerificationSubmitResult = PhoneVerificationResult;

export interface PhoneVerificationCommandResponse<Result> {
  data: {
    type: 'commands';
    id: string;
    attributes: {
      command:
        'start-phone-verification' | 'check-phone-verification' | 'submit-phone-verification';
      status: 'pending' | 'success' | 'error';
      message: string;
      result: Result | { verificationState: 'not_verified' };
      commandErrors: PhoneVerificationCommandError[];
      metadata: { requestedAt: string; requestedBy: string | null };
    };
  };
}

/** Drops absent fields so an omitted surface/platform never posts a null. */
function commandBody(fields: Record<string, string | undefined>): string {
  const body: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value) {
      body[key] = value;
    }
  }

  return JSON.stringify(body);
}

/**
 * Starts a profile phone-verification challenge with POST
 * `/api/v2/commands/phone-verification/start` and skips the request cache.
 * Omitting `phoneNumber` asks the API to use the active mobile number.
 * Consumers can classify a later rejected store request with
 * `phoneVerificationErrorCode`, and the surface renders that inline — so the request
 * opts out of the global error toast.
 */
export function startPhoneVerificationRequest(
  phoneNumber?: string,
  origin: PhoneVerificationOrigin = {}
): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/start`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: commandBody({ phoneNumber, surface: origin.surface, platform: origin.platform }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Stores a number and starts its challenge in one call, with POST
 * `/api/v2/commands/phone-verification/submit`, skipping the request cache.
 *
 * This is the entry point for a *new or replacement* number: unlike `start`, it
 * persists the number as `unverified` before sending the code, so a member who
 * abandons the code step still has a number on file and a deferred link can reach
 * them. `country` is an ISO-3166 alpha-2 code and disambiguates a `+1` number
 * between US and Canada, which share the NANP.
 *
 * Consumers can classify a later rejected store request with
 * `phoneVerificationErrorCode`, and the surface renders that inline — so the request
 * opts out of the global error toast.
 */
export function submitPhoneVerificationRequest(
  phoneNumber: string,
  options: PhoneVerificationOrigin & { country?: string } = {}
): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/submit`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: commandBody({
      number: phoneNumber,
      country: options.country,
      surface: options.surface,
      platform: options.platform,
    }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Checks a 4–10 digit code with POST `/api/v2/commands/phone-verification/check`
 * and skips the request cache. The success envelope reports whether the stored
 * phone number changed. Consumers can classify a later rejected store request
 * with `phoneVerificationErrorCode`, and the surface renders that inline — so the
 * request opts out of the global error toast.
 */
export function checkPhoneVerificationRequest(
  code: string,
  phoneNumber?: string,
  origin: PhoneVerificationOrigin = {}
): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/check`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: commandBody({ code, phoneNumber, surface: origin.surface, platform: origin.platform }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Asks for a verify-later link to the caller's active mobile number, with POST
 * `/api/v2/commands/phone-verification/request-link`, skipping the request cache.
 *
 * The server only enqueues the text: success means requested, not delivered. It rejects
 * with `invalid_phone_number` (no active mobile) or `rate_limit_exceeded`, which callers
 * treat as soft, so the request opts out of the global error toast.
 */
export function requestPhoneVerificationLinkRequest(
  origin: PhoneVerificationOrigin = {}
): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/request-link`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: commandBody({ surface: origin.surface, platform: origin.platform }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Unwraps a successful phone-verification command result for the caller's known command shape.
 *
 * @throws when the response is not a command envelope with a result.
 */
export function parsePhoneVerificationResult<Result>(content: unknown): Result {
  return parseCommandResult<Result>(content);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPhoneVerificationErrorCode(value: unknown): value is PhoneVerificationErrorCode {
  return typeof value === 'string' && phoneVerificationErrorCodes.has(value);
}

/** Unwraps a WarpDrive request failure to the command envelope it carries, if any. */
function commandEnvelope(error: unknown): Record<string, unknown> | undefined {
  const content = isRecord(error) && 'content' in error ? error['content'] : error;
  if (
    !isRecord(content) ||
    !isRecord(content['data']) ||
    !isRecord(content['data']['attributes'])
  ) {
    return undefined;
  }

  return content['data']['attributes'];
}

/**
 * Reads the server's retry/cooldown figures out of a *rejected* command response, so a
 * wrong code can still show the decremented attempt count and a lockout can start its
 * countdown. Returns `undefined` when the failure is not a command envelope, or carries
 * no `verification` block — callers must then leave whatever they already had.
 */
export function phoneVerificationAttempt(
  error: unknown
): PhoneVerificationAttemptPayload | undefined {
  const result = commandEnvelope(error)?.['result'];
  if (!isRecord(result) || !isRecord(result['verification'])) {
    return undefined;
  }

  return result['verification'] as PhoneVerificationAttemptPayload;
}

/**
 * Reads the stable capability error code from a command response or WarpDrive
 * request failure. Returns `undefined` for authentication, transport, or malformed
 * errors so callers do not surface provider or transport error text.
 */
export function phoneVerificationErrorCode(error: unknown): PhoneVerificationErrorCode | undefined {
  const errors = commandEnvelope(error)?.['commandErrors'];
  if (
    !Array.isArray(errors) ||
    !isRecord(errors[0]) ||
    !isPhoneVerificationErrorCode(errors[0]['type'])
  ) {
    return undefined;
  }

  return errors[0]['type'];
}
